import { Controller } from "@hotwired/stimulus"

// Person picker: opens a modal with debounced search and either POSTs the
// chosen person to `submitUrl` (server-driven) or dispatches a custom event
// `person-picker:choose` with detail `{ personId, personName }` (form-driven).
//
// Targets:
//   modal    - the backdrop+panel of the modal
//   search   - the search input inside the modal
//   results  - container where server-rendered result html is injected
//
// Values:
//   searchUrl    : URL used for live search (returns html partial)
//   submitUrl    : optional URL for POST when a person is chosen
//   extraParams  : JSON object merged into the POST body
//   excludeIds   : JSON array of person ids to hide from the result list
//   turboFrame   : optional Turbo frame to target on response
export default class extends Controller {
  static targets = ["modal", "search", "results"]
  static values = {
    searchUrl: String,
    submitUrl: String,
    extraParams: { type: String, default: "{}" },
    excludeIds: { type: String, default: "[]" },
    turboFrame: String,
    debounceMs: { type: Number, default: 200 }
  }

  connect() {
    this._debounceTimer = null
    this._abortController = null
  }

  open(event) {
    event?.preventDefault()
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
      this._renderInitialMessage()
    }
  }

  close() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) this.close()
  }

  escClose(event) {
    if (event.key === "Escape" && this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  search() {
    clearTimeout(this._debounceTimer)
    const query = this.searchTarget.value.trim()
    this._debounceTimer = setTimeout(() => this._performSearch(query), this.debounceMsValue)
  }

  async _performSearch(query) {
    if (this._abortController) this._abortController.abort()
    this._abortController = new AbortController()

    const url = new URL(this.searchUrlValue, window.location.origin)
    if (query) url.searchParams.set("q", query)
    this._excludeIds().forEach((id) => url.searchParams.append("exclude_ids[]", id))

    try {
      const response = await fetch(url.toString(), {
        headers: { "Accept": "text/html" },
        signal: this._abortController.signal
      })
      if (!response.ok) throw new Error(`Search failed: ${response.status}`)
      const html = await response.text()
      if (this.hasResultsTarget) this.resultsTarget.innerHTML = html
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err)
        if (this.hasResultsTarget) {
          this.resultsTarget.innerHTML = `<div class="px-4 py-6 text-center text-xs text-red-500">${this._escape(err.message)}</div>`
        }
      }
    }
  }

  async choose(event) {
    const { personId, personName } = event.params

    this.dispatch("choose", { detail: { personId, personName } })

    if (this.hasSubmitUrlValue && this.submitUrlValue.length > 0) {
      await this._submitChoice(personId)
    }

    this.close()
  }

  async _submitChoice(personId) {
    const params = this._extraParams()
    params.person_id = personId

    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((item) => body.append(`${k}[]`, item))
      } else if (v !== null && v !== undefined) {
        body.append(k, v)
      }
    })

    const headers = { "Accept": "text/vnd.turbo-stream.html, text/html" }
    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    if (csrf) headers["X-CSRF-Token"] = csrf
    if (this.hasTurboFrameValue) headers["Turbo-Frame"] = this.turboFrameValue

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: headers,
        body: body
      })

      if (!response.ok) {
        console.error(`Person picker submit failed: ${response.status}`)
        return
      }

      const contentType = response.headers.get("Content-Type") || ""
      const text = await response.text()

      if (contentType.includes("turbo-stream")) {
        const { Turbo } = await import("@hotwired/turbo-rails")
        Turbo.renderStreamMessage(text)
      } else if (this.hasTurboFrameValue) {
        const frame = document.getElementById(this.turboFrameValue)
        if (frame) {
          frame.innerHTML = text
        }
      } else {
        window.location.reload()
      }
    } catch (err) {
      console.error(err)
    }
  }

  _excludeIds() {
    try {
      return JSON.parse(this.excludeIdsValue || "[]")
    } catch (_) {
      return []
    }
  }

  _extraParams() {
    try {
      return JSON.parse(this.extraParamsValue || "{}")
    } catch (_) {
      return {}
    }
  }

  _renderInitialMessage() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = `<div class="px-4 py-10 text-center text-xs text-zinc-400">${this._escape(this.element.dataset.personPickerStartTyping || "")}</div>`
    this._performSearch("")
  }

  _escape(text) {
    const div = document.createElement("div")
    div.textContent = text == null ? "" : String(text)
    return div.innerHTML
  }
}
